// src/services/metalRates.js
// Provides live metal rates for Gold, Silver, Platinum.
// Optionally integrates with external APIs when environment variables are configured.
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

const PLATINUM_TO_GOLD_RATIO = 45400 / 11000; // fallback platinum:gold price ratio
const DEFAULT_STATE_RATE_FIELD = 'rtgsSale';
const OZ_TO_GRAM = 31.103; // Standard troy ounce to gram conversion (same as SuperAdmin)

// Metal Premium Defaults (fetched from Firebase)
const DEFAULT_PREMIUMS = {
  gold: 0,
  silver: 0,
  platinum: 0,
};

const STATE_NAME_TO_CODE = {
  'andhra pradesh': 'AP',
  'arunachal pradesh': 'AR',
  assam: 'AS',
  bihar: 'BR',
  chhattisgarh: 'CG',
  goa: 'GA',
  gujarat: 'GJ',
  haryana: 'HR',
  'himachal pradesh': 'HP',
  jharkhand: 'JH',
  karnataka: 'KA',
  kerala: 'KL',
  'madhya pradesh': 'MP',
  maharashtra: 'MH',
  manipur: 'MN',
  meghalaya: 'ML',
  mizoram: 'MZ',
  nagaland: 'NL',
  odisha: 'OD',
  orissa: 'OD',
  punjab: 'PB',
  rajasthan: 'RJ',
  sikkim: 'SK',
  'tamil nadu': 'TN',
  telangana: 'TS',
  tripura: 'TR',
  'uttar pradesh': 'UP',
  uttarakhand: 'UK',
  'west bengal': 'WB',
  delhi: 'DL',
  'new delhi': 'DL',
  chandigarh: 'CH',
  'jammu & kashmir': 'JK',
  'jammu and kashmir': 'JK',
  ladakh: 'LA',
  puducherry: 'PY',
};

// Shared cache so every caller (Dashboard, Live Rates) reads the exact same
// current rates within the window. This keeps the computed change identical
// across components.
let cachedRates = null;
let cachedAt = 0;
let cachedPremiums = DEFAULT_PREMIUMS;
const CACHE_TTL_MS = 1000;

export function normalizeStateCode(state) {
  const raw = String(state || '').trim();
  if (!raw) return '';
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  return STATE_NAME_TO_CODE[raw.toLowerCase()] || raw.toUpperCase();
}

/**
 * Fetch metal premiums from Firebase
 */
async function fetchMetalPremiums() {
  try {
    const snap = await getDoc(doc(db, 'systemConfig', 'metalPremiums'));
    if (snap.exists()) {
      const data = snap.data();
      const premiumsData = data.premiums || {}; // SuperAdmin stores as { premiums: {...} }
      cachedPremiums = {
        gold: Number(premiumsData.gold ?? DEFAULT_PREMIUMS.gold),
        silver: Number(premiumsData.silver ?? DEFAULT_PREMIUMS.silver),
        platinum: Number(premiumsData.platinum ?? DEFAULT_PREMIUMS.platinum),
      };
      return cachedPremiums;
    }
  } catch (err) {
    console.warn('[fetchMetalPremiums] Failed to fetch premiums:', err);
  }
  return cachedPremiums;
}

export async function getStateRateConstraint(state) {
  const stateCode = normalizeStateCode(state);
  if (!stateCode) return null;

  try {
    const snap = await getDoc(doc(db, 'rateConstraints', stateCode));
    if (!snap.exists()) return { stateCode, active: true };
    const data = snap.data();
    return {
      stateCode,
      stateName: data.stateName || stateCode,
      active: data.active !== false,
      gold: data.gold || {},
      silver: data.silver || {},
      platinum: data.platinum || {},
    };
  } catch (error) {
    console.warn('State rate constraint fetch failed:', error);
    return { stateCode, active: true };
  }
}

/**
 * Apply State Constraint to Indian Base Rate
 * Formula: State Rate = Indian Base Rate + (Indian Base Rate × State Constraint%)
 * Alternative: State Rate = Indian Base Rate × (1 + State Constraint% / 100)
 */
function applyStateConstraint(indiaRate, metalConstraint, rateField = DEFAULT_STATE_RATE_FIELD) {
  const base = Number(indiaRate);
  if (!Number.isFinite(base) || base <= 0) return null;
  const adjustment = Number(metalConstraint?.[rateField]) || 0;
  return Number((base * (1 + adjustment / 100)).toFixed(2));
}

function attachStateRates(rates, constraint) {
  if (!rates) return rates;
  const stateCode = constraint?.stateCode || '';
  const stateName = constraint?.stateName || stateCode || 'State';
  const active = constraint?.active !== false;
  
  const withStateRate = (metalKey) => {
    // rates[metalKey].india is the Indian Base Rate for this metal
    const indiaBaseRate = rates[metalKey]?.india;
    if (!indiaBaseRate) return rates[metalKey];
    
    // Apply constraint to all 4 rate types
    const rtgsSale = active
      ? applyStateConstraint(indiaBaseRate, constraint?.[metalKey], 'rtgsSale')
      : Number((indiaBaseRate * 1.0020).toFixed(2));
    
    const rtgsPurchase = active
      ? applyStateConstraint(indiaBaseRate, constraint?.[metalKey], 'rtgsPurchase')
      : Number((indiaBaseRate * 0.9980).toFixed(2));
    
    const metalSale = active
      ? applyStateConstraint(indiaBaseRate, constraint?.[metalKey], 'metalSale')
      : Number((indiaBaseRate * 1.0010).toFixed(2));
    
    const metalPurchase = active
      ? applyStateConstraint(indiaBaseRate, constraint?.[metalKey], 'metalPurchase')
      : Number((indiaBaseRate * 0.9970).toFixed(2));

    return {
      ...rates[metalKey],
      RTGS_SALE_RATE: rtgsSale,
      RTGS_PURCHASE_RATE: rtgsPurchase,
      METAL_SALE_RATE: metalSale,
      METAL_PURCHASE_RATE: metalPurchase,
      // Keep india field for backward compatibility
      india: indiaBaseRate,
    };
  };

  return {
    ...rates,
    stateCode,
    stateName,
    stateRateField: DEFAULT_STATE_RATE_FIELD,
    gold: withStateRate('gold'),
    silver: withStateRate('silver'),
    platinum: withStateRate('platinum'),
  };
}

// Fallback dataset so the UI always renders real-looking rates even when the
// live API is unreachable (CORS/offline) or when useLiveApi is disabled.
function getFallbackRates(market) {
  const base = { gold: 8950, silver: 102, platinum: 3120 };
  const jitter = (v) => Number((v + (Math.random() - 0.5) * 0.01 * v).toFixed(2));
  const mk = (v) => ({
    central: Number((jitter(v) * 0.98).toFixed(2)),
    india: Number(jitter(v).toFixed(2)),
    local: Number((jitter(v) * 1.01).toFixed(2)),
  });
  return {
    market,
    updatedAt: new Date().toISOString(),
    gold: mk(base.gold),
    silver: mk(base.silver),
    platinum: mk(base.platinum),
    currency: 'INR',
    unit: 'gram',
    isFallback: true,
  };
}

export async function getMetalRates({ market = 'Local Market', useLiveApi = false, state = '' } = {}) {
  const now = Date.now();
  const constraint = await getStateRateConstraint(state);

  if (useLiveApi && cachedRates && now - cachedAt < CACHE_TTL_MS) {
    return attachStateRates({ ...cachedRates, market }, constraint);
  }
  if (!useLiveApi) return attachStateRates(getFallbackRates(market), constraint);

  // Live API implementation
  try {
      // Fetch metal premiums from Firebase
      const premiums = await fetchMetalPremiums();

      // Fetch spot prices in USD and USD/INR exchange rate
      const [goldRes, silverRes, platinumRes, fxRes] = await Promise.all([
        fetch("https://api.gold-api.com/price/XAU"),
        fetch("https://api.gold-api.com/price/XAG"),
        fetch("https://api.gold-api.com/price/XPT"),
        fetch("https://open.er-api.com/v6/latest/USD"),
      ]);

      const goldData = goldRes.ok ? await goldRes.json() : null;
      const silverData = silverRes.ok ? await silverRes.json() : null;
      const platinumData = platinumRes.ok ? await platinumRes.json() : null;
      const fxJson = fxRes.ok ? await fxRes.json() : null;
      const usdToInr = fxJson?.rates?.INR || 83.3; // fallback rate

      if (!usdToInr) {
        throw new Error("Could not fetch USD to INR exchange rate");
      }

      /**
       * Indian Base Rate Calculation (EXACT SAME AS SUPERADMIN)
       * Formula: Indian Base Rate = (Spot USD × USD/INR ÷ 28.35) × (1 + Premium%)
       * Expanded: Indian Base Rate = (Spot USD × USD/INR ÷ 28.35) + (Spot USD × USD/INR ÷ 28.35) × (Premium%)
       * 
       * Step 1: Convert spot price to INR per gram = (Spot USD × USD/INR ÷ 28.35)
       * Step 2: Calculate premium amount = base × (Premium% / 100)
       * Step 3: Indian Base Rate = base + premium amount
       */
      const pricePerGram = (usdPerOz, premiumPct) => {
        if (!usdPerOz) return null;
        const baseInr = (usdPerOz * usdToInr) / OZ_TO_GRAM;
        const premiumAmount = baseInr * (premiumPct / 100);
        return baseInr + premiumAmount;
      };

      // Calculate gold price per gram in INR with premium
      const goldInrPerGram = pricePerGram(goldData?.price, premiums.gold);

      // Calculate silver price per gram in INR with premium
      const silverInrPerGram = pricePerGram(silverData?.price, premiums.silver);

      // Calculate platinum price per gram in INR with premium
      const platinumInrPerGram = pricePerGram(platinumData?.price, premiums.platinum);

      console.log('[getMetalRates] Calculated base rates:', {
        goldSpotUsd: goldData?.price,
        silverSpotUsd: silverData?.price,
        platinumSpotUsd: platinumData?.price,
        usdToInr,
        premiums,
        goldInrPerGram: goldInrPerGram?.toFixed(2),
        silverInrPerGram: silverInrPerGram?.toFixed(2),
        platinumInrPerGram: platinumInrPerGram?.toFixed(2),
      });

      // If we got valid data, return it with variation for local market
      if (goldInrPerGram !== null && silverInrPerGram !== null) {
        // Apply market-specific variations
        const gold = {
          central: Number((goldInrPerGram * 0.98).toFixed(2)), // Central slightly lower
          india: Number((goldInrPerGram * 1.00).toFixed(2)),   // India base rate
          local: Number((goldInrPerGram * 1.01).toFixed(2)),   // Local slightly higher
        };

        const silver = {
          central: Number((silverInrPerGram * 0.98).toFixed(2)),
          india: Number((silverInrPerGram * 1.00).toFixed(2)),
          local: Number((silverInrPerGram * 1.01).toFixed(2)),
        };

        // For platinum, use direct API price or fall back to gold:platinum ratio
        const platinumRate = platinumInrPerGram !== null ? platinumInrPerGram : (goldInrPerGram * PLATINUM_TO_GOLD_RATIO);

        const platinum = {
          central: Number((platinumRate * 0.98).toFixed(2)), // Central slightly lower
          india: Number((platinumRate * 1.00).toFixed(2)),   // India base rate
          local: Number((platinumRate * 1.01).toFixed(2)),   // Local slightly higher
        };

        const result = {
          market,
          updatedAt: new Date().toISOString(),
          gold,
          silver,
          platinum,
          currency: 'INR',
          unit: 'gram',
        };
        cachedRates = result;
        cachedAt = now;
        return attachStateRates(result, constraint);
      }
  } catch (error) {
      console.warn('Live API failed:', error);
  }

  // Fallback - API either failed or returned incomplete data
  return attachStateRates(getFallbackRates(market), constraint);

}
export function formatCurrencyINR(value) {
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(value);
  } catch {
    return `₹${Number(value).toFixed(2)}`;
  }
}
